import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FiAlertTriangle, FiCheckCircle, FiHash, FiX } from 'react-icons/fi';
import api from '../../libs/axios';

// DECISION-001 S5: put every ball back at its ledger position (match 706 was fixed
// by hand this way). The server answers with a preview first; applying needs a
// reason and is recorded in the audit log.

interface Change { id: number; event_counter: number; from: string; to: string }
interface InningsResult {
  innings_number: number;
  balls_checked: number;
  changes: Change[];
  total_changes: number;
  skipped_reason: string | null;
}
interface Answer {
  match_id: number;
  dry_run: boolean;
  positions_mode: string;
  balls_per_over: number;
  total_changes: number;
  innings: InningsResult[];
  applied: { innings_number: number; changed: number }[];
  next_steps: string[];
}

interface Props {
  matchId: string;
  isOpen: boolean;
  onClose: () => void;
  onDone?: () => void;
}

const errorMessage = (e: any, fallback: string) => e?.response?.data?.message || fallback;

const PositionsModal: React.FC<Props> = ({ matchId, isOpen, onClose, onDone }) => {
  const [preview, setPreview] = useState<Answer | null>(null);
  const [result, setResult] = useState<Answer | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [recalcBusy, setRecalcBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res: any = await api.post(`/match-ops/${matchId}/restamp-positions`, { dry_run: true });
      setPreview(res.data as Answer);
    } catch (e: any) {
      setPreview(null);
      setLoadError(errorMessage(e, 'Could not check this match.'));
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => {
    if (!isOpen) return;
    setResult(null);
    setReason('');
    load();
  }, [isOpen, load]);

  if (!isOpen) return null;

  const reasonOk = reason.trim().length >= 5;
  const canApply = !!preview && preview.total_changes > 0 && reasonOk && !submitting && !loading;

  const apply = async () => {
    if (!canApply) return;
    setSubmitting(true);
    try {
      const res: any = await api.post(`/match-ops/${matchId}/restamp-positions`, { dry_run: false, reason: reason.trim() });
      setResult(res.data as Answer);
      toast.success(`Positions re-stamped for match #${matchId}`);
      onDone?.();
    } catch (e: any) {
      toast.error(errorMessage(e, 'Re-stamp failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const recalculate = async () => {
    setRecalcBusy(true);
    try {
      await api.post(`/match-ops/${matchId}/recalculate`, {});
      toast.success('Recalculated');
    } catch (e: any) {
      toast.error(errorMessage(e, 'Recalculate failed'));
    } finally {
      setRecalcBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={submitting ? undefined : onClose} />
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl">
          <button
            onClick={onClose}
            disabled={submitting}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 disabled:opacity-50"
            aria-label="Close"
          >
            <FiX className="w-5 h-5" />
          </button>
          <div className="p-6">
            <div className="flex items-center gap-3 mb-1">
              <FiHash className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900">Recompute ball positions · match #{matchId}</h3>
            </div>
            <p className="text-sm text-gray-500 mb-5">
              Gives every ball the over and ball number that follow from the order it was bowled in (its event
              counter). Use it when a match is stuck on &quot;over complete&quot; refusals. Runs, wickets and balls do not
              change; only the over.ball labels.
            </p>

            {loading && <p className="text-sm text-gray-500">Checking the match…</p>}
            {loadError && <p className="text-sm text-red-600">{loadError}</p>}

            {result ? (
              <div className="space-y-4">
                <div className="flex items-start gap-3 rounded-lg bg-green-50 border border-green-200 p-4 text-sm text-green-900">
                  <FiCheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                  <p className="font-medium">
                    {result.total_changes} ball(s) re-stamped
                    {result.applied.length ? ` (${result.applied.map((a) => `innings ${a.innings_number}: ${a.changed}`).join(', ')})` : ''}.
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Next steps</h4>
                  <ol className="list-decimal pl-5 space-y-1 text-sm text-gray-700">
                    {result.next_steps.map((s) => <li key={s}>{s}</li>)}
                  </ol>
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={recalculate}
                    disabled={recalcBusy}
                    className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm disabled:opacity-50"
                  >
                    {recalcBusy ? 'Recalculating…' : 'Recalculate stats now'}
                  </button>
                  <button onClick={onClose} className="px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-900 text-white text-sm">
                    Done
                  </button>
                </div>
              </div>
            ) : preview ? (
              <div className="space-y-5">
                <p className="text-sm text-gray-700">
                  {preview.total_changes === 0
                    ? 'Every ball already sits at its ledger position. Nothing to change.'
                    : `${preview.total_changes} ball(s) would move. Server positions mode: ${preview.positions_mode}; ${preview.balls_per_over} balls per over.`}
                </p>
                {preview.innings.map((inn) => (
                  <div key={inn.innings_number}>
                    <h4 className="text-sm font-semibold text-gray-900 mb-1">
                      Innings {inn.innings_number}
                      <span className="font-normal text-gray-500"> · {inn.balls_checked} balls checked · {inn.total_changes} to change</span>
                    </h4>
                    {inn.skipped_reason && <p className="text-xs text-amber-700">{inn.skipped_reason}</p>}
                    {inn.changes.length > 0 && (
                      <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 text-gray-600 sticky top-0">
                            <tr>
                              <th className="text-left font-medium px-3 py-2">Ball (counter)</th>
                              <th className="text-left font-medium px-3 py-2">Now</th>
                              <th className="text-left font-medium px-3 py-2">Becomes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {inn.changes.map((c) => (
                              <tr key={c.id} className="border-t border-gray-100">
                                <td className="px-3 py-1.5">{c.event_counter}</td>
                                <td className="px-3 py-1.5 text-gray-500">{c.from}</td>
                                <td className="px-3 py-1.5 font-medium">{c.to}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {inn.total_changes > inn.changes.length && (
                      <p className="text-xs text-gray-500 mt-1">Showing the first {inn.changes.length}.</p>
                    )}
                  </div>
                ))}
                {preview.next_steps.filter((s) => s.startsWith('Server positions')).map((s) => (
                  <div key={s} className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                    <FiAlertTriangle className="w-4 h-4 mt-0.5" />
                    <span>{s}</span>
                  </div>
                ))}
                {preview.total_changes > 0 && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-1" htmlFor="restamp-reason">
                      Reason <span className="font-normal text-gray-500">(kept in the audit log)</span>
                    </label>
                    <textarea
                      id="restamp-reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      maxLength={500}
                      rows={2}
                      placeholder="e.g. Over 36 refused as complete; scorer confirmed the balls"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      disabled={submitting}
                    />
                    {!reasonOk && reason.length > 0 && <p className="text-xs text-gray-500 mt-1">At least 5 characters.</p>}
                  </div>
                )}
                <div className="flex justify-end gap-3">
                  <button
                    onClick={onClose}
                    disabled={submitting}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50 disabled:opacity-50"
                  >
                    {preview.total_changes > 0 ? 'Cancel' : 'Close'}
                  </button>
                  {preview.total_changes > 0 && (
                    <button
                      onClick={apply}
                      disabled={!canApply}
                      className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? 'Applying…' : `Apply to ${preview.total_changes} ball(s)`}
                    </button>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PositionsModal;
